import React, { useEffect, useState } from "react";

import { StateLanguage } from "../../languages/config/StateLanguage";
import { SettingsDA } from "../../services/admin/settings.service";
import { Error } from "../../models/error.model";
import { GlobalSwal } from "../../styles/swal.style";
import { legendInvalid, legendIconInvalid, legendIconValidInvalidRestart, legendValidInvalidRestart } from "../../utils/legend.data.util";
import { modalHide } from "../../utils/modal.util";
import { evaluateLegendRequiredIcon } from "../../scripts/validate.legend.script";
import { expressions } from "../../libraries/regular.expressions.library";
import { getError, getMessage } from "../../languages/translations/response";
import { errorWebHandler } from "../../scripts/error.web.handler.script";
import { PaymentDeadline } from "../../models/settings.payment-deadlines.model";
import { Company } from "../../models/company.model";

import AppLegend from "../../components/element/Legend";
import AppInputDeadlineType from "../element/InputDeadlineType";

export interface AppModalSettingsDeadlineProps {
  companyForUser: Company | null;
  deadlineItem: PaymentDeadline | null;
  onSuccess: (mode: "create" | "edit") => void;
}

let errorResponse: Error;

const AppModalSettingsDeadline: React.FunctionComponent<AppModalSettingsDeadlineProps> = ({companyForUser, deadlineItem, onSuccess }) => {
    const { lang } = StateLanguage();

    const [loadIndicator, setLoadIndicator] = useState("off");
    const [type, setType] = useState({ value: "", valid: false });
    const [quantity, setQuantity] = useState({ value: "", valid: false });

    const isEditMode = deadlineItem !== null;

    const submitDeadline = async () => {
        setLoadIndicator("on");

        if (companyForUser && type.valid && quantity.valid) {
            const request = isEditMode
                ? SettingsDA.putSettingPaymentDeadline(companyForUser.id, deadlineItem!.id, type.value, quantity.value)
                : SettingsDA.postSettingPaymentDeadline(companyForUser.id, type.value, quantity.value);

            await request
                .then((response) => {
                    if (response.status === 200 || response.status === 201) {
                        const mode = isEditMode ? "edit" : "create";

                        onSuccess(mode);

                        GlobalSwal.fire({
                            title: isEditMode ? getMessage("Successfully updated payment deadline", lang.code) : getMessage("Successfully created payment deadline", lang.code),
                            text: lang.labels.actionCompletedReturningToPage,
                            icon: "success",
                            showConfirmButton: false,
                            timer: 2200,
                        }).then(() => {
                            executeHideModal();
                        });
                    } else {
                        errorResponse = response.data;

                        GlobalSwal.fire({
                            title: getError(errorResponse.code, lang.code, errorResponse.message),
                            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                            icon: "error",
                            confirmButtonText: lang.labels.okGotIt,
                        });
                    }
                })
                .catch((error) => {
                    errorWebHandler(lang, error);
                })
                .finally(() => {
                    setLoadIndicator("off");
                });
        } else {
            GlobalSwal.fire({
                text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
                icon: "error",
                showConfirmButton: false,
                timer: 2200,
            }).then(() => {
                if (!type.value.length) legendInvalid("modal-settings-deadline-legend-type-required");
                if (!quantity.value.length)
                    legendIconInvalid("modal-settings-deadline-input-quantity", "modal-settings-deadline-legend-quantity-required");
                setLoadIndicator("off");
            });
        }
    };

    const handleChangeQuantity = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
        setQuantity({ ...quantity, value: event.target.value });
    };

    const validateQuantity = () => {
        evaluateLegendRequiredIcon(
            expressions.number,
            quantity,
            setQuantity,
            "modal-settings-deadline-input-quantity",
            "modal-settings-deadline-legend-quantity-valid",
            "modal-settings-deadline-legend-quantity-required",
        );
    };

    const executeHideModal = () => {
        modalHide("modal-settings-deadline");

        setTimeout(() => {
            restartModal();
        }, 200);
    };

    function restartModal() {
        legendValidInvalidRestart("modal-settings-deadline-legend-type-required");
        legendIconValidInvalidRestart("modal-settings-deadline-input-quantity", "modal-settings-deadline-legend-quantity-valid");
        legendIconValidInvalidRestart("modal-settings-deadline-input-quantity", "modal-settings-deadline-legend-quantity-required");

        setType({ value: "", valid: false });
        setQuantity({ value: "", valid: false });
    }

    useEffect(() => {
        if (deadlineItem) {
            setType({ value: deadlineItem.type, valid: true });
            setQuantity({ value: deadlineItem.quantity.toString(), valid: true });
        } else {
            restartModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deadlineItem]);

    return (
        <div
            id="modal-settings-deadline"
            className="modal fade"
            tabIndex={-1}
            aria-hidden="true"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
        >
            <div className="modal-dialog modal-md">
                <div className="modal-content">
                    <div className="modal-header">
                        <h2 className="text-capitalize">
                            {isEditMode ? lang.labels.editDeadlinePaymentParameter : lang.labels.addDeadlinePaymentParameter}
                        </h2>
                    </div>
                    <div className="modal-body">
                        <div className="form">
                            <div className="row mb-2">
                                <label className="col-lg-3 col-form-label required">{lang.labels.type}</label>
                                <div className="col-lg-9">
                                    <AppInputDeadlineType
                                        component={"modal-settings-deadline"}
                                        options={null}
                                        typeDeadline={type}
                                        setTypeDeadline={setType}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <label className="col-lg-3 col-form-label required">{lang.labels.quantity}</label>
                                <div className="col-lg-9">
                                    <input
                                        id="modal-settings-deadline-input-quantity"
                                        className="form-control"
                                        type="text"
                                        autoComplete="off"
                                        name="quantity"
                                        value={quantity.value}
                                        onChange={handleChangeQuantity}
                                        onKeyUp={validateQuantity}
                                        onBlur={validateQuantity}
                                    />
                                    <AppLegend
                                        component={"modal-settings-deadline"}
                                        attribute={{ check: quantity.valid, name: "quantity", index: null, sub_index: null }}
                                        container={{ valid: true, required: true, identical: false, exists: false }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer flex-center">
                        <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>
                            {lang.labels.discard}
                        </button>
                        <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitDeadline}>
                            <span className="indicator-label">{isEditMode ? lang.labels.saveChanges : lang.labels.create}</span>
                            <span className="indicator-progress">
                                {lang.labels.pleaseWait}
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppModalSettingsDeadline;
